/**
 * Pure functions for computing the next cell position during keyboard navigation.
 * These are extracted from the table controller to enable unit testing.
 */

type NextCell = { row: number; colIndex: number } | null;

/**
 * Compute the next cell when Tab / Shift+Tab is pressed.
 * Tab moves right; at end of row wraps to first navigable column of next row.
 * Shift+Tab moves left; at first navigable column wraps to last column of previous row.
 * Returns null when navigation is impossible (boundary reached).
 */
export function computeNextCellOnTab(
  currentRow: number,
  currentColIndex: number,
  numRows: number,
  numCols: number,
  shiftKey: boolean,
  firstNavigableColIndex: number = 1,
): NextCell {
  if (currentColIndex < 0 || numCols <= firstNavigableColIndex || numRows === 0) return null;

  const lastNavigableColIndex = numCols - 1;

  if (shiftKey) {
    if (currentColIndex <= firstNavigableColIndex) {
      if (currentRow > 0) {
        return { row: currentRow - 1, colIndex: lastNavigableColIndex };
      }
      return null;
    }
    return { row: currentRow, colIndex: currentColIndex - 1 };
  }

  if (currentColIndex >= lastNavigableColIndex) {
    if (currentRow < numRows - 1) {
      return { row: currentRow + 1, colIndex: firstNavigableColIndex };
    }
    return null;
  }
  return { row: currentRow, colIndex: currentColIndex + 1 };
}

/**
 * Compute the next cell when Enter / Shift+Enter is pressed.
 * Enter moves down; at last row wraps to first row of next column.
 * Shift+Enter moves up; at first row wraps to last row of previous column.
 * Returns null when navigation is impossible (boundary reached).
 */
export function computeNextCellOnEnter(
  currentRow: number,
  currentColIndex: number,
  numRows: number,
  numCols: number,
  shiftKey: boolean,
  firstNavigableColIndex: number = 1,
): NextCell {
  if (currentColIndex < firstNavigableColIndex || numCols <= firstNavigableColIndex || numRows === 0) return null;

  const lastNavigableColIndex = numCols - 1;

  if (shiftKey) {
    if (currentRow <= 0) {
      if (currentColIndex <= firstNavigableColIndex) return null;
      return { row: numRows - 1, colIndex: currentColIndex - 1 };
    }
    return { row: currentRow - 1, colIndex: currentColIndex };
  }

  if (currentRow >= numRows - 1) {
    if (currentColIndex >= lastNavigableColIndex) return null;
    return { row: 0, colIndex: currentColIndex + 1 };
  }
  return { row: currentRow + 1, colIndex: currentColIndex };
}
